/** Spec §14.2 page 1: connection health, capabilities, reconnect instructions, identity. */
import { useCapabilities, useDevice } from "../api/hooks.js";
import { StateBadge } from "../components/StateBadge.js";
import { Empty, PageState } from "../components/PageState.js";

export function DevicesPage({ deviceId }: { deviceId: string }) {
  const device = useDevice(deviceId);
  const caps = useCapabilities(deviceId);

  if (!deviceId) return <Empty label="No device selected." />;

  return (
    <PageState query={{ isPending: device.isPending, error: device.error }}>
      <section className="stack">
        <h2>{device.data?.nickname ?? "Device"}</h2>
        <div className="row">
          <span>identity: <span className="mono">{device.data?.id}</span></span>
          <StateBadge state={device.data?.credential_status ?? "unenrolled"} />
          <StateBadge state={device.data?.operational_state ?? "UNENROLLED"} />
        </div>
        <p className="muted">
          Model {device.data?.model ?? "not reported"} · OS {device.data?.os_version ?? "not reported"} ·
          enrolled {device.data?.enrolled_at ? new Date(device.data.enrolled_at).toLocaleString() : "—"} ·
          last seen {device.data?.last_seen_at ? new Date(device.data.last_seen_at).toLocaleString() : "never"}
        </p>
        <p className="muted mono">coverage {device.data?.coverage?.mode ?? "UNKNOWN"} — {device.data?.coverage?.mode_note ?? ""}</p>
        <ul className="muted mono">
          {(device.data?.coverage?.planes ?? []).map((plane) => (
            <li key={plane.plane}>
              {plane.plane}: {plane.state}
              {plane.reason ? `(${plane.reason})` : ""}
            </li>
          ))}
        </ul>
        <p className="notice">
          Reconnect: open the QuestGuardian companion on the headset, confirm the pairing code shown in
          Settings, and let it complete enrollment over Wi-Fi. Storage and battery are reported by the
          headset agent only during a live heartbeat.
        </p>
      </section>
      <section>
        <h3>Verified capabilities</h3>
        {(caps.data ?? []).length === 0 ? (
          <p className="notice">No capability probes recorded yet — run the device diagnostics to populate.</p>
        ) : (
          <table>
            <thead>
              <tr><th>Capability</th><th>State</th><th>Firmware</th><th>Checked</th><th>Notes</th></tr>
            </thead>
            <tbody>
              {(caps.data ?? []).map((c) => (
                <tr key={c.capability}>
                  <td className="mono">{c.capability}</td>
                  <td><StateBadge state={c.state} /></td>
                  <td>{c.firmware ?? "—"}</td>
                  <td>{new Date(c.checked_at).toLocaleString()}</td>
                  <td>{c.notes ?? ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </PageState>
  );
}
